import { useMemo } from "react";
import useSWR from "swr";
import { Agent, fetchAgentsByIds } from "~/services/agents";
import { FeaturedSession } from "~/services/projects";

// A featured session references its agent by id only (see `FeaturedSession`),
// so the rail must resolve it itself. Most rows resolve for free against the
// project's already-loaded agents; only ids missing from that set (e.g. an
// agent later detached from the project) trigger the batch lookup, and a caller
// who can't view that agent gets `undefined` back — rendered without an avatar.
export const useFeaturedSessionAgents = (
  featuredSessions: FeaturedSession[] | undefined,
  attachedAgentsWithDetails: Agent[] | undefined
): Map<string, Agent> => {
  const attachedById = useMemo(
    () => new Map((attachedAgentsWithDetails ?? []).map((a) => [a.id, a])),
    [attachedAgentsWithDetails]
  );

  const missingAgentIds = useMemo(
    () =>
      [
        ...new Set(
          (featuredSessions ?? [])
            .map((f) => f.agent_id)
            .filter((id): id is string => id != null && !attachedById.has(id))
        ),
      ].sort(),
    [featuredSessions, attachedById]
  );

  const { data: fetchedAgents } = useSWR<Agent[]>(
    missingAgentIds.length ? ["featured-session-agents", ...missingAgentIds] : null,
    ([, ...ids]: [string, ...string[]]) => fetchAgentsByIds(ids)
  );

  return useMemo(() => {
    const map = new Map(attachedById);
    for (const agent of fetchedAgents ?? []) {
      map.set(agent.id, agent);
    }
    return map;
  }, [attachedById, fetchedAgents]);
};
